import { BALANCE, BUILDING_DEFS } from '../constants';
import type { Simulation } from '../sim';
import type { Peep, SimState } from '../types';
import { releaseTaskClaims } from './claims';
import { pushLedger } from './economy';

function releaseClaims(state: SimState, peep: Peep): void {
  releaseTaskClaims(state, peep);
}

/** Saloon and casino money has a dark side; the sheriff keeps a lid on it. */
/** Nuclear scrams: recovery first, then an independent daily roll on any
 * running plant. Draws RNG only when a plant exists, so pre-nuclear price
 * and incident sequences are untouched. */
function stepScrams(sim: Simulation): void {
  const state = sim.state;
  const day = sim.day;
  const plants = state.buildings.filter((building) => building.type === 'nuclearPlant');
  if (plants.length === 0) return;
  for (const plant of plants) {
    if (plant.outageUntilDay !== null && day >= plant.outageUntilDay) {
      plant.outageUntilDay = null;
      sim.networksDirty = true;
      sim.pushEvent({ kind: 'toast', message: 'The reactor is back online.' });
    }
  }
  const def = BUILDING_DEFS.nuclearPlant;
  const running = plants.filter(
    (plant) =>
      plant.outageUntilDay === null && plant.staffAssigned.length >= def.staffRequired && plant.watered,
  );
  if (running.length === 0) return;
  const rng = sim.forks.incidents;
  if (!rng.chance(BALANCE.scramChancePerWeek / 7)) return;
  const plant = running[0]!;
  plant.outageUntilDay = day + rng.integer(BALANCE.scramOutageDaysMin, BALANCE.scramOutageDaysMax);
  sim.networksDirty = true;
  state.incidents.log.push({ day, kind: 'scram', note: 'Reactor scram — the plant went dark for safety checks.' });
  if (state.incidents.log.length > BALANCE.incidentLogCap) state.incidents.log.shift();
  sim.pushEvent({ kind: 'scram', buildingId: plant.id });
  sim.pushEvent({ kind: 'toast', message: 'SCRAM! The reactor tripped offline for safety checks.' });
}

export function stepIncidentsDaily(sim: Simulation): void {
  const state = sim.state;
  const day = sim.day;

  stepScrams(sim);

  // Off-duty workers report back.
  for (const peep of state.peeps) {
    if (peep.task.kind === 'offDuty' && day >= peep.task.untilDay) {
      peep.task = { kind: 'idle' };
    }
  }

  if (state.peeps.length === 0) return;
  if (day - state.incidents.lastIncidentDay < BALANCE.incidentMinGapDays) return;

  const saloonOpen = state.buildings.some((building) => building.type === 'saloon' && building.functional);
  const casinoOpen = state.buildings.some((building) => building.type === 'casino' && building.functional);
  const sheriffOpen = state.buildings.some((building) => building.type === 'sheriff' && building.functional);
  const probability =
    (BALANCE.incidentBase + (saloonOpen ? BALANCE.incidentSaloon : 0) + (casinoOpen ? BALANCE.incidentCasino : 0)) *
    (1.5 - state.workforce.morale) *
    (sheriffOpen ? BALANCE.incidentSheriffFactor : 1);
  const rng = sim.forks.incidents;
  if (!rng.chance(probability)) return;

  state.incidents.lastIncidentDay = day;
  const hospitalOpen = state.buildings.some((building) => building.type === 'hospital' && building.functional);
  const roll = rng.next();
  let note: string;
  let kind: 'brawl' | 'theft' | 'noShow';
  if (roll < 0.5) {
    kind = 'brawl';
    const candidates = state.peeps.filter((peep) => peep.role !== 'child' && peep.task.kind !== 'offDuty');
    const victim = rng.pick(candidates);
    let outDays = rng.integer(2, 4);
    if (hospitalOpen) outDays = Math.max(1, Math.ceil(outDays / 2));
    releaseClaims(state, victim);
    victim.task = { kind: 'offDuty', untilDay: day + outDays };
    victim.path = null;
    note = hospitalOpen
      ? `${victim.name} got patched up after a brawl — out ${outDays} day${outDays === 1 ? '' : 's'}.`
      : `${victim.name} got hurt in a saloon brawl — out ${outDays} days.`;
  } else if (roll < 0.8) {
    kind = 'theft';
    const amount = Math.min(Math.floor(state.economy.cashCents * BALANCE.theftCashFraction), BALANCE.theftMaxCents);
    if (amount > 0) {
      state.economy.cashCents -= amount;
      pushLedger(state, day, 'incident', -amount, 'Cash box robbed');
    }
    note = `Someone robbed the cash box overnight.`;
  } else {
    kind = 'noShow';
    let benched = 0;
    for (const peep of state.peeps) {
      if (peep.role !== 'worker' || peep.job === 'staff' || peep.task.kind === 'offDuty') continue;
      if (rng.chance(0.2)) {
        releaseClaims(state, peep);
        peep.task = { kind: 'offDuty', untilDay: day + 1 };
        peep.path = null;
        benched += 1;
      }
    }
    note = benched > 0 ? `${benched} workers are too hungover to work today.` : 'Half the camp looks hungover.';
  }

  state.incidents.log.push({ day, kind, note });
  if (state.incidents.log.length > BALANCE.incidentLogCap) state.incidents.log.shift();
  sim.pushEvent({ kind: 'incident', incident: kind, note });
  sim.pushEvent({ kind: 'toast', message: note });
}
